import { useEffect, useState } from "react";
import { api, type SavedSearch } from "../lib/api";
import SavedSearchForm from "../components/SavedSearchForm";

export default function SavedSearches() {
  const [items, setItems] = useState<SavedSearch[]>([]);
  const [editing, setEditing] = useState<SavedSearch | null>(null);
  const [creating, setCreating] = useState<SavedSearch | true | null>(null);

  async function refresh() {
    setItems(await api.listSavedSearches());
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate(input: Parameters<typeof api.createSavedSearch>[0]) {
    await api.createSavedSearch(input);
    setCreating(null);
    await refresh();
  }

  /** Open the New form pre-populated from an existing saved search.
   *  We strip the name (UNIQUE constraint) and the active flag (so the
   *  duplicate doesn't immediately start scanning before the user is
   *  done editing). Everything else carries over. */
  function handleDuplicate(src: SavedSearch) {
    setEditing(null);
    setCreating({
      ...src,
      name: `${src.name} (copy)`,
      active: false,
    });
  }

  async function handleUpdate(input: Parameters<typeof api.updateSavedSearch>[1]) {
    if (!editing) return;
    await api.updateSavedSearch(editing.id, input);
    setEditing(null);
    await refresh();
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this saved search?")) return;
    await api.deleteSavedSearch(id);
    await refresh();
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-mono">Saved searches</h1>
        <button
          onClick={() => setCreating(true)}
          className="bg-zinc-800 hover:bg-zinc-700 px-3 py-1 rounded font-mono"
        >
          New
        </button>
      </header>

      {creating && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
          <h2 className="font-mono mb-3">
            {creating === true ? "New saved search" : `Duplicate of ${(creating as SavedSearch).name.replace(/\s*\(copy\)\s*$/, "")}`}
          </h2>
          <SavedSearchForm
            initial={creating === true ? undefined : (creating as SavedSearch)}
            onSubmit={handleCreate}
            onCancel={() => setCreating(null)}
          />
        </div>
      )}

      {editing && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
          <h2 className="font-mono mb-3">Edit: {editing.name}</h2>
          <SavedSearchForm
            initial={editing}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      <ul className="divide-y divide-zinc-800 border border-zinc-800 rounded-md">
        {items.map((s) => (
          <li key={s.id} className="flex items-center gap-3 px-3 py-2 font-mono text-sm">
            <span className={`inline-block w-2 h-2 rounded-full ${s.active ? "bg-green-500" : "bg-zinc-600"}`} />
            <span className="flex-1">{s.name}</span>
            <span className="text-zinc-500">{s.category}</span>
            <button onClick={() => setEditing(s)} className="text-zinc-400 hover:text-zinc-200">edit</button>
            <button onClick={() => handleDuplicate(s)} className="text-zinc-400 hover:text-zinc-200">duplicate</button>
            <button onClick={() => void handleDelete(s.id)} className="text-red-400 hover:text-red-300">delete</button>
          </li>
        ))}
        {items.length === 0 && (
          <li className="px-3 py-4 text-zinc-500 text-sm">No saved searches yet.</li>
        )}
      </ul>
    </div>
  );
}
